import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  searchUsers, 
  getPendingRequests, 
  sendFriendRequest, 
  respondFriendRequest,
  getFriendsList
} from "../../api/communityApi";

const FriendsTab = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Fetch initial data
  useEffect(() => {
    fetchPendingRequests();
    fetchFriends();
    // Fetch some default users (empty query might return some random users if backend allows or we just wait for search)
    handleSearch(""); 
  }, []);

  // Delay search to avoid spamming API
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearch(search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const fetchPendingRequests = async () => {
    try {
      const data = await getPendingRequests();
      setFriendRequests(data.requests || []);
    } catch (error) {
      console.error("Failed to fetch requests", error);
    }
  };

  const fetchFriends = async () => {
    try {
      const data = await getFriendsList();
      setFriends(data.friends || []);
    } catch (error) {
      console.error("Failed to fetch friends", error);
    }
  };

  const handleSearch = async (query) => {
    try {
      setLoading(true);
      const data = await searchUsers(query);
      setSearchResults(data.users || []);
    } catch (error) {
      console.error("Failed to search users", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId) => {
    try {
      await sendFriendRequest(userId);
      setMessage("Friend request sent!");
      handleSearch(search); // refresh search results to update status
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Failed to send request", error);
      setMessage("Failed to send request.");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const handleRespond = async (friendshipId, action) => {
    try {
      await respondFriendRequest(friendshipId, action);
      setMessage(`Request ${action}ed!`);
      fetchPendingRequests();
      if (action === 'accept') fetchFriends();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error(`Failed to ${action} request`, error);
      setMessage(`Failed to ${action} request`);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  return (
    <div className="space-y-10 animate-fadeIn">
      {message && (
        <div className="p-4 bg-green-900/40 border border-green-700 rounded-xl text-green-300">
          {message}
        </div>
      )}

      {/* SEARCH BAR */}
      <div>
        <h2 className="text-3xl font-bold mb-2">Add Friends</h2>
        <p className="text-gray-400 mb-4">
          Search and connect with investors using FinAI.
        </p>

        <input
          type="text"
          placeholder="Search users by username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="
            w-full px-5 py-3 rounded-lg 
            bg-gray-100 dark:bg-gray-800 
            border border-gray-300 dark:border-gray-700
            focus:ring-2 focus:ring-indigo-500
            transition
          "
        />
      </div>

      {/* FRIEND REQUESTS */}
      <div>
        <h3 className="text-xl font-semibold mb-3">Friend Requests</h3>

        {friendRequests.length === 0 ? (
          <p className="text-gray-500">No new friend requests</p>
        ) : (
          <div className="space-y-4">
            {friendRequests.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between 
                  p-4 bg-gray-100 dark:bg-gray-800 
                  rounded-xl border border-gray-300 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={req.avatar_url || `https://ui-avatars.com/api/?name=${req.username}`}
                    className="w-12 h-12 rounded-full"
                    alt={req.username}
                  />
                  <div>
                    <p className="font-semibold">{req.first_name ? `${req.first_name} ${req.last_name}` : req.username}</p>
                    <p className="text-gray-400 text-sm">Wants to connect</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button 
                    onClick={() => handleRespond(req.id, 'accept')}
                    className="px-4 py-1 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white"
                  >
                    Accept
                  </button>
                  <button 
                    onClick={() => handleRespond(req.id, 'reject')}
                    className="px-4 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
                  >
                    Ignore
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MY FRIENDS */}
      <div>
        <h3 className="text-xl font-semibold mb-3">My Friends</h3>
        
        {friends.length === 0 ? (
          <p className="text-gray-500 mb-6">You haven't added any friends yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {friends.map((friend) => (
              <div
                key={friend.id}
                className="p-6 bg-gray-100 dark:bg-gray-800 
                  rounded-xl shadow border border-gray-300 dark:border-gray-700 
                  hover:shadow-lg transition flex flex-col justify-between"
              >
                <div
                  className="flex items-center gap-3 mb-4 cursor-pointer hover:opacity-80 transition"
                  onClick={() => navigate(`/community/profile/${friend.id}`)}
                >
                  <img
                    src={friend.avatar_url || `https://ui-avatars.com/api/?name=${friend.username}`}
                    alt={friend.username}
                    className="w-14 h-14 rounded-full"
                  />
                  <div>
                    <p className="text-lg font-semibold truncate w-32">
                      {friend.first_name ? `${friend.first_name} ${friend.last_name}` : friend.username}
                    </p>
                    {friend.thirty_day_return !== null && friend.thirty_day_return !== undefined && (
                      <p className={`text-sm font-medium ${friend.thirty_day_return >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {friend.thirty_day_return >= 0 ? "+" : ""}{friend.thirty_day_return}% (30d)
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/community/profile/${friend.id}`)}
                  className="w-full py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition font-medium"
                >
                  View Profile
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SUGGESTED / SEARCHED INVESTORS */}
      <div>
        <h3 className="text-xl font-semibold mb-3">
          {search ? "Search Results" : "Suggested Investors"}
        </h3>
        
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : searchResults.length === 0 ? (
          <p className="text-gray-500">No users found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchResults.map((user) => (
              <div
                key={user.id}
                className="p-6 bg-gray-100 dark:bg-gray-800 
                  rounded-xl shadow border border-gray-300 dark:border-gray-700 
                  hover:shadow-lg transition flex flex-col justify-between"
              >
                {/* Avatar */}
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.username}`}
                    alt={user.username}
                    className="w-14 h-14 rounded-full"
                  />
                  <div>
                    <p className="text-lg font-semibold truncate w-32">
                      {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
                    </p>
                    {user.thirty_day_return !== null && (
                      <p className={`text-sm font-medium ${user.thirty_day_return >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {user.thirty_day_return >= 0 ? "+" : ""}{user.thirty_day_return}% (30d)
                      </p>
                    )}
                  </div>
                </div>

                {user.friendship_status === "accepted" ? (
                  <button disabled className="w-full py-2 bg-gray-700 rounded-lg text-gray-300">
                    Friends
                  </button>
                ) : user.friendship_status === "pending" ? (
                  <button disabled className="w-full py-2 bg-indigo-900 rounded-lg text-indigo-300">
                    {user.friendship_direction === 'sent' ? 'Request Sent' : 'Pending Reply'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSendRequest(user.id)}
                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white transition"
                  >
                    ➕ Add Friend
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI MATCHMAKING POINTER — feature is live in the row at the top of Community */}
      <div className="mt-10 p-6 bg-indigo-900/20 border border-indigo-700 rounded-xl relative overflow-hidden">
        <div className="relative z-10">
          <h3 className="text-xl font-semibold text-indigo-300 mb-2">
            AI Matchmaking is live
          </h3>
          <p className="text-gray-300 text-sm">
            See investors with similar (or opposite) strategies in the <b>AI Matchmaking</b> row at the top of Community.
            FinAI matches on broad sector allocations, shared holdings, and investing style.
            <i> (Your absolute portfolio values are never shared.)</i> You can turn matchmaking off anytime in Settings → Privacy.
          </p>
        </div>
      </div>

    </div>
  );
};

export default FriendsTab;
